import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import Loading from '@/components/Loading';
import ExportV2 from '@/components/Studies/Export/ExportV2';
import ExportsList from '@/components/Studies/StudyExports/ExportsList/ExportsList';
import { getTabIndex, type Tab as StudyExportsTab } from '@/components/Studies/StudyExports/StudyExports.utils';
import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import { _isNil, _notNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { ResponseType } from '@/support/LegacyApiService';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const StudyExports: FC = () => {
  const { id: studyId } = useParams<{ id: string }>();

  const { response: studyResponse, loading: studyLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.show.p',
    path: { id: studyId },
    query: { include: 'users,metadata,study_groups' },
    options: { invokeOnInit: true, onError: { toast: false, slug: 'study-exports-study-load' } },
  });

  const studyLoadError = studyResponse?.type === ResponseType.Error ? studyResponse.error : undefined;
  const study =
    studyResponse?.type === ResponseType.Success
      ? (studyResponse.body as { data: Study } | undefined)?.data
      : undefined;

  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  const tabs: Array<StudyExportsTab> = useMemo(
    () =>
      _notNil(study)
        ? [
            { id: 'newExport', name: 'New Export', element: <ExportV2 study={study} /> },
            { id: 'exportsHistory', name: 'Export History', element: <ExportsList study={study} /> },
          ]
        : [],
    [study]
  );

  const [tabState, setTabState] = useState(getTabIndex(search, tabs));

  useEffect(() => {
    const newTabState = getTabIndex(search, tabs);
    setTabState((prev) => (newTabState !== prev ? newTabState : prev));
  }, [search, tabs.length]);

  useEffect(() => {
    if (tabs.length === 0) {
      return;
    }
    navigate({ pathname, search: `tab=${tabs[tabState].id}` });
  }, [tabState]);

  if (studyLoading) {
    return (
      <div className="w-100 h-100">
        <Loading />
      </div>
    );
  }

  if (_isNil(study) && _notNil(studyLoadError)) {
    return <ApiErrorBanner errorType={ApiErrorType.FETCH} error={studyLoadError} />;
  }

  if (_isNil(study)) {
    return null;
  }

  return (
    <div className="ph4 pt3" data-testid="study-exports">
      <Tabs outerState={[tabState, setTabState]}>
        <div className="mb4 bb b--moon-gray">
          {tabs.map(({ id, name }) => (
            <Tab className="f4" key={`${id}_tab`} testId={`study-exports__${id}-tab`}>
              {name}
            </Tab>
          ))}
        </div>
        {tabs.map(({ id, element }) => (
          <Panel key={`${id}_panel`}>{element}</Panel>
        ))}
      </Tabs>
    </div>
  );
};

export default StudyExports;
